import { useEffect, useId, useState } from 'react';
import { MOCK_FORCED } from '../lib/api';
import { useCompany } from '../lib/companyContext';
import { formatPercentFromBps } from '../lib/format';
import type { BillingModel, CompanySummary, RefundPolicy } from '../types';
import { Badge, Button, Field, Input, Select, SectionCard, Footnote } from './ui';

/**
 * Comissao e cobranca de UMA empresa: o lugar onde a administracao ALTERA e o
 * cartao que a propria empresa VE (pedido do dono, 25/09/2026: "tem que ter
 * onde altera a comissao no painel do adm, e essa comissao tem que aparecer
 * na aba da empresa pra ela sempre ver").
 *
 * Um componente, dois modos:
 *  - `editable` (administracao): percentual em bps, modelo de cobranca e
 *    politica de estorno, com "Salvar" que chama `updateCompany`. Na previa a
 *    alteracao vale em todas as telas (contexto de empresa + KPIs simulados).
 *  - somente leitura (empresa): os mesmos tres fatos, sem formulario, com a
 *    frase que explica de onde vem cada um. Nunca esconde a comissao do lojista.
 *
 * Regras: percentual entre 1,00% e 50,00% (inteiro em bps); modelo de cobranca
 * ainda "a definir" pelo dono; politica de estorno padrao `reverte_chargeback`
 * (ADR-001 E2).
 *
 * Em modo real "Salvar" vira `PATCH /api/admin/companies/:id` (pelo contexto):
 * o selo "Salvo" so aparece quando o servidor confirmou; se falhar, o contexto
 * desfaz e mostra o aviso, e o formulario continua com o que foi digitado.
 */

const MODELO: Readonly<Record<BillingModel, { readonly label: string; readonly explica: string }>> = {
  contrato: { label: 'Contrato mensal', explica: 'fatura no fim do mês com o extrato das vendas recuperadas' },
  automatico: { label: 'Automático', explica: 'a comissão é retida no provedor a cada venda recuperada' },
  a_definir: { label: 'A definir', explica: 'o dono ainda vai escolher entre contrato mensal e automático' },
};

const POLITICA: Readonly<Record<RefundPolicy, { readonly label: string; readonly explica: string }>> = {
  nao_reverte: { label: 'Nunca reverte', explica: 'reembolso e chargeback ficam registrados, a comissão permanece' },
  reverte_chargeback: { label: 'Só chargeback reverte (padrão)', explica: 'reembolso não reverte; chargeback (fraude) devolve a comissão' },
  reverte_tudo: { label: 'Reembolso e chargeback revertem', explica: 'qualquer estorno total devolve a comissão' },
};

const MIN_BPS = 100;
const MAX_BPS = 5_000;

function bpsParaTexto(bps: number): string {
  return (bps / 100).toFixed(2).replace('.', ',');
}

function textoParaBps(texto: string): number | null {
  const limpo = texto.replace('%', '').trim().replace(',', '.');
  if (!/^\d{1,2}([.]\d{0,2})?$/.test(limpo)) {
    return null;
  }
  const bps = Math.round(Number(limpo) * 100);
  return Number.isInteger(bps) && bps >= MIN_BPS && bps <= MAX_BPS ? bps : null;
}

export function EditorDeComissao({ company, editable }: { readonly company: CompanySummary; readonly editable: boolean }): JSX.Element {
  const { updateCompany } = useCompany();
  const politicaAtual: RefundPolicy = company.refundPolicy ?? 'reverte_chargeback';
  const [fee, setFee] = useState(bpsParaTexto(company.feeBps));
  const [modelo, setModelo] = useState<BillingModel>(company.billingModel);
  const [politica, setPolitica] = useState<RefundPolicy>(politicaAtual);
  const [salvoEm, setSalvoEm] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const idAviso = useId();

  useEffect(() => {
    setFee(bpsParaTexto(company.feeBps));
    setModelo(company.billingModel);
    setPolitica(company.refundPolicy ?? 'reverte_chargeback');
  }, [company.id, company.feeBps, company.billingModel, company.refundPolicy]);

  useEffect(() => {
    if (salvoEm === null) {
      return undefined;
    }
    const t = window.setTimeout(() => setSalvoEm(null), 2_200);
    return () => window.clearTimeout(t);
  }, [salvoEm]);

  const bps = textoParaBps(fee);
  const erroFee = bps === null ? 'Informe entre 1,00% e 50,00% (até duas casas).' : null;
  const mudou = bps !== null && (bps !== company.feeBps || modelo !== company.billingModel || politica !== politicaAtual);

  const salvar = (): void => {
    if (bps === null || salvando) {
      return;
    }
    setSalvando(true);
    void updateCompany(company.id, { feeBps: bps, billingModel: modelo, refundPolicy: politica }).then((ok) => {
      setSalvando(false);
      if (ok) {
        setSalvoEm(Date.now());
      }
    });
  };

  if (!editable) {
    return (
      <SectionCard
        id="comissao-da-empresa"
        title="Sua comissão com o Converte Fácil"
        icon="comissao"
        description="Definida pela administração do Converte Fácil. Aparece aqui para você sempre saber quanto fica com quem recuperou a venda."
        aside={<Badge tone="brand">{formatPercentFromBps(company.feeBps)} sobre o recuperado</Badge>}
      >
        <dl className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-line bg-elevated px-4 py-3">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão</dt>
            <dd className="mt-1 text-2xl font-semibold tracking-tight text-ink">{formatPercentFromBps(company.feeBps)}</dd>
            <dd className="text-xs text-ink-subtle">só sobre vendas recuperadas em até 7 dias após um contato</dd>
          </div>
          <div className="rounded-xl border border-line bg-elevated px-4 py-3">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Cobrança</dt>
            <dd className="mt-1 text-base font-semibold text-ink">{MODELO[company.billingModel].label}</dd>
            <dd className="text-xs text-ink-subtle">{MODELO[company.billingModel].explica}</dd>
          </div>
          <div className="rounded-xl border border-line bg-elevated px-4 py-3">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Estornos</dt>
            <dd className="mt-1 text-base font-semibold text-ink">{POLITICA[politicaAtual].label}</dd>
            <dd className="text-xs text-ink-subtle">{POLITICA[politicaAtual].explica}</dd>
          </div>
        </dl>
        <div className="mt-3">
          <Footnote>Quer renegociar? Fale com a equipe do Converte Fácil; a mudança passa a valer para as vendas recuperadas a partir da data acordada e nunca altera comissões já apuradas.</Footnote>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      id="editor-de-comissao"
      title="Comissão e cobrança"
      icon="comissao"
      description={`O que ${company.name} paga ao Converte Fácil. A alteração aparece na hora no painel da própria empresa.`}
      aside={
        salvoEm === null ? (
          <Badge tone="neutral">Atual: {formatPercentFromBps(company.feeBps)}</Badge>
        ) : (
          <Badge tone="good" dot>
            {MOCK_FORCED ? 'Salvo na prévia' : 'Salvo'}
          </Badge>
        )
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          salvar();
        }}
        className="grid gap-4 md:grid-cols-3"
        aria-describedby={idAviso}
      >
        <Field label="Comissão sobre o recuperado" required hint="Entre 1,00% e 50,00%. Congelada em cada venda no momento da conversão." error={erroFee}>
          {({ id, describedBy, invalid }) => (
            <div className="relative">
              <Input id={id} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} aria-describedby={describedBy} aria-invalid={invalid} className="pr-8 tabular-nums" />
              <span aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">
                %
              </span>
            </div>
          )}
        </Field>
        <Field label="Modelo de cobrança" hint={MODELO[modelo].explica}>
          {({ id, describedBy }) => (
            <Select id={id} value={modelo} onChange={(e) => setModelo(e.target.value as BillingModel)} aria-describedby={describedBy}>
              {(Object.keys(MODELO) as BillingModel[]).map((m) => (
                <option key={m} value={m}>
                  {MODELO[m].label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Política de estorno" hint={POLITICA[politica].explica}>
          {({ id, describedBy }) => (
            <Select id={id} value={politica} onChange={(e) => setPolitica(e.target.value as RefundPolicy)} aria-describedby={describedBy}>
              {(Object.keys(POLITICA) as RefundPolicy[]).map((p) => (
                <option key={p} value={p}>
                  {POLITICA[p].label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="flex flex-wrap items-center gap-3 md:col-span-3">
          <Button type="submit" disabled={!mudou || salvando} icon="check">
            {salvando ? 'Salvando…' : 'Salvar comissão'}
          </Button>
          <Button type="button" variant="ghost" disabled={!mudou} onClick={() => { setFee(bpsParaTexto(company.feeBps)); setModelo(company.billingModel); setPolitica(politicaAtual); }}>
            Descartar
          </Button>
          <p id={idAviso} className="text-xs text-ink-subtle">
            Vale para as próximas vendas recuperadas; comissões já apuradas não mudam.{MOCK_FORCED ? ' Na prévia, fica salvo neste navegador.' : ''}
          </p>
        </div>
      </form>
    </SectionCard>
  );
}
